import { ErrorInfo } from 'react';

/**
 * Error reporting utility for centralized error handling and logging.
 *
 * This module provides utilities for reporting errors consistently across the application.
 * It can be extended to integrate with error reporting services like Sentry, LogRocket, etc.
 */

export interface ErrorReport {
  error: Error;
  errorInfo?: ErrorInfo;
  context?: Record<string, unknown>;
  userId?: string;
  timestamp: Date;
}

/**
 * Reports an error to the error tracking service.
 * In production, this should integrate with services like Sentry, LogRocket, etc.
 *
 * @param error - The error object
 * @param errorInfo - Optional React error info
 * @param context - Additional context about the error
 */
export const reportError = (
  error: Error,
  errorInfo?: ErrorInfo,
  context?: Record<string, unknown>,
): void => {
  const errorReport: ErrorReport = {
    error,
    errorInfo,
    context,
    timestamp: new Date(),
    // userId can be extracted from AuthContext if needed
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error Report:', errorReport);
  }

  // In production, send to error reporting service
  // Example integrations:
  //
  // Sentry:
  // Sentry.captureException(error, {
  //   contexts: { react: errorInfo },
  //   extra: context,
  // });
  //
  // LogRocket:
  // LogRocket.captureException(error, {
  //   tags: context,
  // });
  //
  // Custom API:
  // fetch('/api/errors', {
  //   method: 'POST',
  //   body: JSON.stringify(errorReport),
  // });
};

/**
 * Creates an error handler function that can be used with Error Boundaries.
 */
export const createErrorHandler = (context?: Record<string, unknown>) => {
  return (error: Error, errorInfo: ErrorInfo) => {
    reportError(error, errorInfo, context);
  };
};

/**
 * Wraps an async function to catch and report errors.
 * Useful for event handlers and async operations that Error Boundaries can't catch.
 */
export const withErrorHandling = <
  T extends (...args: unknown[]) => Promise<unknown>,
>(
  fn: T,
  context?: Record<string, unknown>,
): T => {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (error instanceof Error) {
        reportError(error, undefined, {
          ...context,
          functionName: fn.name,
          arguments: args,
        });
      }
      throw error; // Re-throw to allow caller to handle
    }
  }) as T;
};

/**
 * Wraps a synchronous function to catch and report errors.
 */
export const withSyncErrorHandling = <
  T extends (...args: unknown[]) => unknown,
>(
  fn: T,
  context?: Record<string, unknown>,
): T => {
  return ((...args: Parameters<T>) => {
    try {
      return fn(...args);
    } catch (error) {
      if (error instanceof Error) {
        reportError(error, undefined, {
          ...context,
          functionName: fn.name,
          arguments: args,
        });
      }
      throw error; // Re-throw to allow caller to handle
    }
  }) as T;
};
